/**
 * useAgentStream Hook
 * Custom hook for managing agent streaming communication
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import os from 'os';
import { agentService, extractErrorMessage } from '../../agent/service.js';
import type { McpState, McpToolResult, AgentStreamCallbacks } from '../../agent/types.js';
import { createToolCallTracker } from '../ToolCallDisplay.js';
import type { ToolCallRecord } from '../components/ToolCallIndicator.js';
import { onLoopPrompt, setAgentBusyChecker } from '../../mcp/loop.js';
import { onContextAction, consumeCompactSummary } from '../../mcp/context-tools.js';

export interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    isStreaming?: boolean;
    toolCalls?: Array<{
        id: string;
        name: string;
        type: 'tool' | 'mcp';
        serverName?: string;
        status: 'pending' | 'completed' | 'error';
    }>;
}

export interface UseAgentStreamOptions {
    mcpState?: McpState;
    onMcpToolCall?: (
        callId: string,
        serverName: string,
        toolName: string,
        args: Record<string, unknown>
    ) => Promise<McpToolResult>;
    cliMode?: boolean;
    showToolCalls?: boolean;
    resumeConversationId?: string;
    modelTier?: 'lite' | 'flash' | 'pro';
}

export interface UseAgentStreamReturn {
    messages: Message[];
    isProcessing: boolean;
    currentToolCall: { name: string; type: 'tool' | 'mcp'; serverName?: string } | null;
    toolCallHistory: ToolCallRecord[];
    streamingText: string;
    conversationId: string | undefined;
    sendMessage: (message: string) => Promise<void>;
    abort: () => void;
    addRemoteExchange: (userMessage: string, assistantContent: string, clientType: string) => void;
    queuedMessage: string | null;
    enqueueMessage: (message: string) => void;
    clearQueuedMessage: () => void;
}

const MAX_MESSAGE_HISTORY = 100;

function trimMessages(msgs: Message[]): Message[] {
    if (msgs.length <= MAX_MESSAGE_HISTORY) return msgs;
    return msgs.slice(msgs.length - MAX_MESSAGE_HISTORY);
}

export function useAgentStream(options: UseAgentStreamOptions = {}): UseAgentStreamReturn {
    // Disable console.log-based tool display in Ink mode - we use ToolCallIndicator component instead
    const { mcpState, onMcpToolCall, cliMode = false, showToolCalls = false, resumeConversationId, modelTier } = options;

    const [messages, setMessages] = useState<Message[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentToolCall, setCurrentToolCall] = useState<{ name: string; type: 'tool' | 'mcp'; serverName?: string } | null>(null);
    const [toolCallHistory, setToolCallHistory] = useState<ToolCallRecord[]>([]);
    const [streamingText, setStreamingText] = useState('');
    const [conversationId, setConversationId] = useState<string | undefined>(resumeConversationId);

    const conversationIdRef = useRef<string | undefined>(resumeConversationId);
    const toolTrackerRef = useRef(createToolCallTracker());

    // Message queue: allows the user to type their next message while a
    // request is in-flight. When the current request finishes, the queued
    // message is sent automatically (inspired by Gemini CLI design).
    const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
    const queuedMessageRef = useRef<string | null>(null);

    const enqueueMessage = useCallback((msg: string) => {
        queuedMessageRef.current = msg;
        setQueuedMessage(msg);
    }, []);

    const clearQueuedMessage = useCallback(() => {
        queuedMessageRef.current = null;
        setQueuedMessage(null);
    }, []);

    /** Add or update a tool call record in history */
    const trackToolCall = useCallback((
        id: string,
        name: string,
        type: 'tool' | 'mcp',
        status: 'pending' | 'completed' | 'error',
        serverName?: string,
        extra?: { args?: Record<string, unknown>; result?: unknown },
    ) => {
        setToolCallHistory(prev => {
            const existing = prev.findIndex(t => t.id === id);
            if (existing >= 0) {
                const updated = [...prev];
                const entry = { ...updated[existing], status };
                if (extra?.result !== undefined) {
                    entry.result = extra.result;
                    if (entry.timestamp) entry.durationMs = Date.now() - entry.timestamp;
                }
                updated[existing] = entry;
                return updated;
            }
            return [...prev, {
                id, name, type, serverName, status,
                timestamp: Date.now(),
                args: extra?.args,
                result: extra?.result,
            }];
        });
    }, []);

    const abortSafetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const abort = useCallback(() => {
        agentService.abort();
        setIsProcessing(false);
        setCurrentToolCall(null);
        setStreamingText('');

        // Safety net: if a previous abort didn't clean up (e.g. hanging MCP call),
        // force-reset after a short delay. Mirrors Gemini CLI's approach of always
        // guaranteeing the UI returns to idle state after cancel.
        if (abortSafetyTimerRef.current) clearTimeout(abortSafetyTimerRef.current);
        abortSafetyTimerRef.current = setTimeout(() => {
            abortSafetyTimerRef.current = null;
            setIsProcessing(false);
            setCurrentToolCall(null);
        }, 2000);
    }, []);

    const sendMessage = useCallback(async (message: string) => {
        // If there's a pending compact summary from a previous paean_context_compact
        // call, prepend it so the new conversation starts with context.
        const compactSummary = consumeCompactSummary();
        const effectiveMessage = compactSummary
            ? `[Context from previous conversation]\n${compactSummary}\n\n[New message]\n${message}`
            : message;

        // Add user message
        const userMessageId = `user-${Date.now()}`;
        setMessages(prev => trimMessages([...prev, {
            id: userMessageId,
            role: 'user',
            content: message,
        }]));

        setIsProcessing(true);
        setStreamingText('');
        setToolCallHistory([]);
        toolTrackerRef.current.clear();

        let responseText = '';

        const callbacks: AgentStreamCallbacks = {
            onContent: (text, partial) => {
                if (partial) {
                    responseText += text;
                } else {
                    responseText = text;
                }
                setStreamingText(responseText);
            },

            onToolCall: (id, name) => {
                setCurrentToolCall({ name, type: 'tool' });
                trackToolCall(id, name, 'tool', 'pending');
                if (showToolCalls) {
                    toolTrackerRef.current.start(id, name, 'local');
                }
            },

            onToolResult: (id, _name, result) => {
                setCurrentToolCall(null);
                const isError = result && typeof result === 'object' &&
                    (result as Record<string, unknown>).isError === true;
                trackToolCall(id, _name, 'tool', isError ? 'error' : 'completed', undefined, { result });
                if (showToolCalls) {
                    if (isError) {
                        toolTrackerRef.current.error(id, 'Tool returned error');
                    } else {
                        toolTrackerRef.current.complete(id, result);
                    }
                }
            },

            onMcpToolCall: async (callId, serverName, toolName, args) => {
                setCurrentToolCall({ name: toolName, type: 'mcp', serverName });
                trackToolCall(callId, toolName, 'mcp', 'pending', serverName, { args });
                if (showToolCalls) {
                    toolTrackerRef.current.start(callId, toolName, 'mcp', args, serverName);
                }

                if (onMcpToolCall) {
                    try {
                        const result = await onMcpToolCall(callId, serverName, toolName, args);
                        setCurrentToolCall(null);
                        trackToolCall(callId, toolName, 'mcp', result.isError ? 'error' : 'completed', serverName, { result });
                        if (showToolCalls) {
                            if (result.isError) {
                                const errorText = result.content[0]?.text || 'Unknown error';
                                toolTrackerRef.current.error(callId, errorText);
                            } else {
                                toolTrackerRef.current.complete(callId, result);
                            }
                        }
                        return result;
                    } catch (error) {
                        setCurrentToolCall(null);
                        trackToolCall(callId, toolName, 'mcp', 'error', serverName);
                        if (showToolCalls) {
                            toolTrackerRef.current.error(callId, (error as Error).message);
                        }
                        return {
                            content: [{ type: 'text' as const, text: `Error: ${(error as Error).message}` }],
                            isError: true,
                        };
                    }
                }

                setCurrentToolCall(null);
                trackToolCall(callId, toolName, 'mcp', 'error', serverName);
                return {
                    content: [{ type: 'text' as const, text: 'MCP not available' }],
                    isError: true,
                };
            },

            onGrounding: (sources) => {
                if (sources.length > 0) {
                    const sourceLines = sources
                        .filter(s => s.title || s.url)
                        .map(s => `  - ${s.title || s.url}${s.url ? ` (${s.url})` : ''}`)
                        .join('\n');
                    if (sourceLines) {
                        const groundingInfo = `\n\nSources:\n${sourceLines}\n`;
                        setStreamingText(prev => prev + groundingInfo);
                    }
                }
            },

            onDone: (convId?: string) => {
                if (convId) {
                    conversationIdRef.current = convId;
                    setConversationId(convId);
                }

                // Add assistant message
                const assistantMessageId = `assistant-${Date.now()}`;
                setMessages(prev => trimMessages([...prev, {
                    id: assistantMessageId,
                    role: 'assistant',
                    content: responseText,
                }]));

                setIsProcessing(false);
                setCurrentToolCall(null);
                setToolCallHistory([]);
                toolTrackerRef.current.clear();
                setStreamingText('');
            },

            onSparkletCreated: (hashKey, title) => {
                // Add sparklet notification to current message
                const sparkletInfo = `\n✨ Sparklet created: ${title} (${hashKey.slice(0, 8)}...)\n`;
                setStreamingText(prev => prev + sparkletInfo);
            },

            onMediaGenerated: (media) => {
                // Add media generation notification to current message
                const typeLabel = media.assetType === 'image' ? 'Image' : 'Video';
                const fileInfo = media.fileName ? ` (${media.fileName})` : '';
                const mediaInfo = `\n🎨 ${typeLabel} generated${fileInfo}\n`;
                setStreamingText(prev => prev + mediaInfo);
            },

            onError: (error) => {
                const errorMessageId = `error-${Date.now()}`;
                const errorText = typeof error === 'string' ? error : extractErrorMessage(error);
                setMessages(prev => trimMessages([...prev, {
                    id: errorMessageId,
                    role: 'assistant',
                    content: `Error: ${errorText}`,
                }]));

                setIsProcessing(false);
                setCurrentToolCall(null);
                toolTrackerRef.current.clear();
                setStreamingText('');
            },
        };

        // Inactivity timeout: if the stream produces no events for 3 minutes,
        // auto-abort and report the timeout. Prevents silent hangs from
        // server disconnections or network stalls.
        const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;
        let lastActivityTime = Date.now();
        const inactivityTimer = setInterval(() => {
            if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT_MS) {
                clearInterval(inactivityTimer);
                agentService.abort();
                callbacks.onError?.('Request timed out due to inactivity. The server may be unresponsive.');
            }
        }, 10_000);

        // Wrap callbacks to track activity
        const wrappedCallbacks: AgentStreamCallbacks = {
            ...callbacks,
            onContent: (text, partial) => { lastActivityTime = Date.now(); callbacks.onContent?.(text, partial); },
            onToolCall: (id, name) => { lastActivityTime = Date.now(); callbacks.onToolCall?.(id, name); },
            onToolResult: (id, name, result) => { lastActivityTime = Date.now(); callbacks.onToolResult?.(id, name, result); },
            onMcpToolCall: async (callId, serverName, toolName, args) => { lastActivityTime = Date.now(); return callbacks.onMcpToolCall!(callId, serverName, toolName, args); },
            onDone: (convId) => { clearInterval(inactivityTimer); callbacks.onDone?.(convId); },
            onError: (error) => { clearInterval(inactivityTimer); callbacks.onError?.(error); },
        };

        try {
            await agentService.streamMessage(effectiveMessage, wrappedCallbacks, {
                conversationId: conversationIdRef.current,
                mcpState,
                cliMode: cliMode ? { enabled: true, cwd: process.cwd(), platform: process.platform, hostname: os.hostname(), channel: 'cli' } : undefined,
                modelTier,
            });
        } catch (error) {
            callbacks.onError?.((error as Error).message);
        } finally {
            clearInterval(inactivityTimer);
        }
    }, [mcpState, onMcpToolCall, cliMode, showToolCalls, trackToolCall, modelTier]);

    // Auto-send queued message when the current request finishes
    const prevIsProcessingRef = useRef(isProcessing);
    useEffect(() => {
        if (prevIsProcessingRef.current && !isProcessing && queuedMessageRef.current) {
            const msg = queuedMessageRef.current;
            queuedMessageRef.current = null;
            setQueuedMessage(null);
            sendMessage(msg);
        }
        prevIsProcessingRef.current = isProcessing;
    }, [isProcessing, sendMessage]);

    // Register agent-busy checker so the cron scheduler knows when to skip
    const isProcessingRef = useRef(isProcessing);
    isProcessingRef.current = isProcessing;

    useEffect(() => {
        setAgentBusyChecker(() => isProcessingRef.current);
    }, []);

    // Subscribe to loop prompt events and auto-send when idle.
    // IMPORTANT: Only inject the task prompt — never include loop metadata
    // (schedule, cron expression) in the message sent to the agent.  Including
    // e.g. "[Loop: 0 * * * *]" causes the agent to misinterpret the cron
    // expression as a request to create a new loop instead of executing the task.
    // The Rust CLI already follows this pattern (process_loop_prompt sends only
    // event.prompt).  UI display of the loop label is handled separately.
    useEffect(() => {
        const unsubscribe = onLoopPrompt((event) => {
            if (!isProcessingRef.current) {
                if (event.clear) {
                    conversationIdRef.current = undefined;
                    setConversationId(undefined);
                }
                sendMessage(`[Scheduled task execution] ${event.prompt}`);
            }
        });
        return unsubscribe;
    }, [sendMessage]);

    // Subscribe to context management events (clear / compact)
    useEffect(() => {
        const unsubscribe = onContextAction((event) => {
            if (event.action === 'clear') {
                conversationIdRef.current = undefined;
                setConversationId(undefined);
            } else if (event.action === 'compact') {
                // Reset conversationId — the compact summary is stored via
                // consumeCompactSummary() and will be prepended to the next
                // user message automatically by sendMessage.
                conversationIdRef.current = undefined;
                setConversationId(undefined);
            }
        });
        return unsubscribe;
    }, []);

    const addRemoteExchange = useCallback((userMessage: string, assistantContent: string, clientType: string) => {
        const userMsg: Message = {
            id: `remote-user-${Date.now()}`,
            role: 'user',
            content: userMessage,
        };
        (userMsg as any).source = 'remote';
        (userMsg as any).clientType = clientType;

        const assistantMsg: Message = {
            id: `remote-assistant-${Date.now()}`,
            role: 'assistant',
            content: assistantContent,
        };

        setMessages(prev => trimMessages([...prev, userMsg, assistantMsg]));
    }, []);

    return {
        messages,
        isProcessing,
        currentToolCall,
        toolCallHistory,
        streamingText,
        conversationId,
        sendMessage,
        abort,
        addRemoteExchange,
        queuedMessage,
        enqueueMessage,
        clearQueuedMessage,
    };
}
